import React, { useRef } from 'react';
import styles from './StepFormIncDecButton.module.scss';

type Props = Omit<
  Partial<React.InputHTMLAttributes<HTMLInputElement>>,
  'onChange'
> & {
  min: number;
  max: number;
  onChange: (value: number) => void;
};

export default function StepFormIncDecButton({ ...props }: Props) {
  const localInputRef = useRef<HTMLInputElement>();
  const spanRef = useRef<HTMLSpanElement>();

  const triggerValue = (value: number) => {
    if (!localInputRef.current) return;

    localInputRef.current.value = String(value);
    props.onChange(value);
    updateSpanValue();
  };

  const updateSpanValue = () => {
    if (!spanRef.current) return;
    spanRef.current.textContent = localInputRef.current?.value || null;
  };

  const registerInput = (el: HTMLInputElement | null) => {
    if (!el) return;

    localInputRef.current = el;
    updateSpanValue();
  };

  const registerSpan = (el: HTMLSpanElement | null) => {
    if (!el) return;

    spanRef.current = el;
    updateSpanValue();
  };

  const handleInc = () => {
    if (!localInputRef.current) return;

    const value = localInputRef.current.value
      ? Number(localInputRef.current.value)
      : props.min;

    const newValue = value + 1;
    triggerValue(newValue >= props.max ? props.max : newValue);
  };

  const handleDec = () => {
    if (!localInputRef.current) return;

    const value = localInputRef.current.value
      ? Number(localInputRef.current.value)
      : props.min;

    const newValue = value - 1;
    triggerValue(newValue <= props.min ? props.min : newValue);
  };

  return (
    <div className={styles.container}>
      <button type="button" aria-label="minus" onClick={handleDec}>
        <i className="fonticons fonticons-minus" />
      </button>
      <input
        ref={registerInput}
        defaultValue={props.value}
        hidden
        type="number"
      />
      <span ref={registerSpan} />
      <button type="button" aria-label="plus" onClick={handleInc}>
        <i className="fonticons fonticons-add" />
      </button>
    </div>
  );
}
